import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MultiSelectFilter } from '../../components/ui/MultiSelectFilter';
import { listUsers } from '../accounts/api';
import { formatUserDisplayName } from '../../lib/formatUserDisplayName';
import type { AudienceScope, CommsPurpose, ContactLevel } from '../../types/broadcast';

// Stage 6A v2 §3-§5 — audience filter controls. Contact-level + comms-purpose
// are small fixed sets (toggle chips); AM is a multi-select of staff users.
// Empty = no filter. These narrow the resolved CONTACTS, not the tree.

type AudienceFiltersProps = {
  scope: AudienceScope;
  onChange: (next: AudienceScope) => void;
};

const CONTACT_LEVELS: Array<{ value: ContactLevel; label: string }> = [
  { value: 'brand', label: 'Brand-level' },
  { value: 'group', label: 'Group-level' },
  { value: 'location', label: 'Location-level' },
];

const COMMS_PURPOSES: Array<{ value: CommsPurpose; label: string }> = [
  { value: 'campaign_previews', label: 'Campaign Previews' },
  { value: 'service_comms', label: 'Service Comms' },
  { value: 'billing_comms', label: 'Billing Comms' },
  { value: 'primary', label: 'Primary Contact' },
];

function toggle<T extends string>(list: T[], value: T): T[] {
  return list.includes(value) ? list.filter((v) => v !== value) : [...list, value];
}

export function AudienceFilters({ scope, onChange }: AudienceFiltersProps) {
  const usersQuery = useQuery({ queryKey: ['users'], queryFn: () => listUsers() });

  const amOptions = useMemo(
    () =>
      (usersQuery.data ?? [])
        .map((u) => ({ value: u.uid, label: formatUserDisplayName(u, { full: true }) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [usersQuery.data],
  );

  return (
    <div className="space-y-3 border-t border-slate-200 pt-3">
      <Chips
        title="Contact level"
        options={CONTACT_LEVELS}
        selected={scope.contactLevelFilter}
        onToggle={(v) => onChange({ ...scope, contactLevelFilter: toggle(scope.contactLevelFilter, v) })}
      />
      <Chips
        title="Comms purpose"
        options={COMMS_PURPOSES}
        selected={scope.commsPurposeFilter}
        onToggle={(v) => onChange({ ...scope, commsPurposeFilter: toggle(scope.commsPurposeFilter, v) })}
      />
      <div>
        <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
          Account manager
        </span>
        <MultiSelectFilter
          label="AMs"
          options={amOptions}
          selected={scope.accountManagerFilter}
          onChange={(next) => onChange({ ...scope, accountManagerFilter: next })}
          placeholder="All AMs"
        />
      </div>
    </div>
  );
}

function Chips<T extends string>({
  title,
  options,
  selected,
  onToggle,
}: {
  title: string;
  options: Array<{ value: T; label: string }>;
  selected: T[];
  onToggle: (value: T) => void;
}) {
  return (
    <div>
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-slate-500">
        {title} <span className="text-slate-400">(default: all)</span>
      </span>
      <div className="flex flex-wrap gap-1.5">
        {options.map((o) => {
          const on = selected.includes(o.value);
          return (
            <button
              key={o.value}
              type="button"
              onClick={() => onToggle(o.value)}
              className={`rounded-full border px-3 py-1 text-xs ${
                on
                  ? 'border-slate-900 bg-slate-900 text-white'
                  : 'border-slate-300 text-slate-600 hover:bg-slate-50'
              }`}
            >
              {o.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
